import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Page from "../molecules/Page";
import BadgeAchievementPopup from "../molecules/BadgeAchievementPopup";
import AchievementPopup from "../molecules/AchievementPopup";
import OnboardingPopup from "../molecules/OnboardingPopup";
import DomainStartPopup from "../molecules/DomainStartPopup";
import SearchModal from "../molecules/SearchModal";
import SessionSetupPopup from "../molecules/SessionSetupPopup"; 
import PaywallPopup from "../molecules/PaywallPopup";
import FeedbackPopup from "../molecules/FeedbackPopup";
import PurchaseSurveyPopup from "../molecules/PurchaseSurveyPopup";
// --- NOWE IMPORTY (Widget + Roadmapa + Utils) ---
import GoalProgressWidget from "../molecules/GoalProgressWidget";
import GoalRoadmapModal, { getRoadmapData, DEFAULT_GOAL_BY_DOMAIN, sumSolvedInFields } from "../molecules/GoalRoadmapModal";
import { hasRank } from "../../utils/gamificationConfig";
import { isAccessBlocked, isNoAccessError } from "../../utils/access";
import { useActiveDomain } from "../../utils/domain";
import DashboardForm from "../../utils/DashboardForm";
import DashboardStatus from "../../utils/DashboardStatus";
import { isPlatform } from "@ionic/react";
import { Capacitor } from "@capacitor/core";
import api from "../../api/apiHelper";
import { useUserDashboard } from "../../contexts/UserDashboardContext";
import InitialLoader from "../common/InitialLoader";
import {
  getMotivationalMessage,
  getBadgeStatus,
  getDashboardSubtitle,
  odznakaBrak,
  AUTH_TOKEN_KEY,
  POPUP_DELAY,
} from "../../utils/dashboardUtils";
const apiUrl = process.env.REACT_APP_API_SOURCE;

// Popup z losowym pytaniem otwartym — wyłączony. Zebrał 71 odpowiedzi (luty–maj 2026)
// i temat jest już rozpoznany; teraz potrzebujemy konkretów od kupujących, więc pytamy
// wyłącznie ankietą po zakupie (PurchaseSurveyPopup). Kod zostaje — wystarczy true.
const FEEDBACK_POPUP_ENABLED = false;
const Dashboard =({ setProblemsData, setUserEloDict }) => {
  const pushInitialized = useRef(false);
  const { dashboardData, isLoading: isDashboardLoading, ensureLoaded } = useUserDashboard();
  const [username, setUsername] = useState(() => dashboardData?.username || "");
  const [accountStatus, setAccountStatus] = useState(() => dashboardData?.account_status || null);
  const [mobileProblems, setMobileProblems] = useState(() => dashboardData?.preferences?.mobile_problems ?? false);
  const navigate = useNavigate();
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  // --- STANY UI ---
  const [mode, setMode] = useState("standard");
  const [achievementsToShow, setAchievementsToShow] = useState(null);
  const [badgePopupData, setBadgePopupData] = useState(null);
  const [expandedSummaryIds, setExpandedSummaryIds] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showDomainIntro, setShowDomainIntro] = useState(false);
  const [isPaywallPopupOpen, setPaywallPopupOpen] = useState(false);
  const [popupUserData, setPopupUserData] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showPurchaseSurvey, setShowPurchaseSurvey] = useState(false);
  
  // --- DANE DO SMART ROADMAPY ---
  const [totalProblems, setTotalProblems] = useState(0); 
  const [availableFields, setAvailableFields] = useState([]); 
  const [isRoadmapOpen, setIsRoadmapOpen] = useState(false);
  const [currentGoalId, setCurrentGoalId] = useState("solid");
  const [currentGoalIdPr, setCurrentGoalIdPr] = useState(DEFAULT_GOAL_BY_DOMAIN.PR);
  const activeDomain = useActiveDomain();
  // --- STANY SESJI I SEARCH ---
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [isSessionDataReady, setIsSessionDataReady] = useState(false);
  const sessionDataRef = useRef(null);
  const [searchData, setSearchData] = useState([]); 
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isSearchModalOpen, setSearchModalOpen] = useState(false);
  // --- STANY FORMULARZA ---
  const [selectedFields, setSelectedFields] = useState([]);
  const [levelModifier, setLevelModifier] = useState(0);
  const [customSelectedFields, setCustomSelectedFields] = useState([]);
  const [subfieldsData, setSubfieldsData] = useState({});
  const [selectedSubfields, setSelectedSubfields] = useState([]);
  const [loadingSubfields, setLoadingSubfields] = useState(null);
  const [fullyLoadedFields, setFullyLoadedFields] = useState([]);
  const [showFreezeTooltip, setShowFreezeTooltip] = useState(false);
  const freezeTooltipRef = useRef(null);
  useEffect(() => {
    if (!showFreezeTooltip) return;
    const handleClickOutside = (e) => {
      if (
        freezeTooltipRef.current && !freezeTooltipRef.current.contains(e.target) &&
        !e.target.closest('#freeze-tooltip-button')
      ) {
        setShowFreezeTooltip(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFreezeTooltip]);
  const badgeStatus = accountStatus
    ? getBadgeStatus(accountStatus.current_streak)
    : { image: odznakaBrak };
    
  // --- INITIAL DATA FETCH ---
  useEffect(() => {
    if (pushInitialized.current) return;
    pushInitialized.current = true;
    localStorage.removeItem("problemsData");
    localStorage.removeItem("problemsSession");
  }, []);
  // Self-heal: jeśli wejdziemy tu bez danych (np. zaraz po logowaniu), dociągnij je
  useEffect(() => { ensureLoaded(); }, [ensureLoaded]);
  // Layout effect: dane z kontekstu muszą być w stanie przed pierwszym malowaniem,
  // inaczej widget celu i działy migały wartościami domyślnymi przy każdym wejściu.
  useLayoutEffect(() => {
    if (!dashboardData) return;
    const data = dashboardData;
    setUsername(data.username);
    setTotalProblems(data.problems_solved_total);
    setAvailableFields(data.available_fields);
    localStorage.setItem("available_fields", JSON.stringify(data.available_fields));
    const backendGoal = data.account_status?.onboarding_goal_id;
    if (backendGoal) {
      setCurrentGoalId(backendGoal);
      localStorage.setItem('user_current_roadmap_goal', backendGoal);
    } else {
      const savedGoal = localStorage.getItem('user_current_roadmap_goal') || "solid";
      setCurrentGoalId(savedGoal);
    }
    // Rozszerzenie ma wlasny cel (progi rekrutacyjne zamiast zdawalnosci).
    setCurrentGoalIdPr(data.account_status?.onboarding_goal_id_pr || DEFAULT_GOAL_BY_DOMAIN.PR);
    setSelectedFields(
      data.selected_fields.map((field) => ({
        label: `${field.field_name} [ELO: ${field.user_field_elo}]`,
        value: field.field_id,
        elo: field.user_field_elo,
      }))
    );
    setLevelModifier(data.preferences.level_modifier);
    setMobileProblems(data.preferences.mobile_problems);
    setAccountStatus(data.account_status);
    if (data.account_status) {
      localStorage.setItem("userStatus", JSON.stringify(data.account_status));
      const pData = {
        username: data.username,
        problems_solved_total: data.problems_solved_total,
        total_study_time_minutes: data.total_study_time_minutes,
        current_streak: data.account_status.current_streak,
      };
      setPopupUserData(pData);
      localStorage.setItem("paywallUserData", JSON.stringify(pData));
    }
    if (data.selected_custom_subfields_details?.length > 0) {
      const subfieldIds = data.selected_custom_subfields_details.map((s) => s.subfield_id);
      setSelectedSubfields(subfieldIds);
      const initialSubfieldsData = {};
      const parentFieldIds = [...new Set(data.selected_custom_subfields_details.map((s) => s.field_id))];
      parentFieldIds.forEach((fieldId) => {
        initialSubfieldsData[fieldId] = data.selected_custom_subfields_details.filter((s) => s.field_id === fieldId);
      });
      setSubfieldsData(initialSubfieldsData);
    }
  }, [dashboardData]);
  useEffect(() => {
    let popupTimerId = null;
    if (!isDashboardLoading && accountStatus) {
      popupTimerId = setTimeout(() => {
        const onboardingFlag = localStorage.getItem("showOnboarding");
        if (onboardingFlag === "true") {
          setShowOnboarding(true);
          localStorage.removeItem("showOnboarding");
          return;
        }
        const newBadgeData = localStorage.getItem("newBadgeToShow");
        if (newBadgeData) {
            setBadgePopupData(JSON.parse(newBadgeData));
            localStorage.removeItem("newBadgeToShow");
        }
        const achievementsData = localStorage.getItem("newAchievementsToShow");
        if (achievementsData) {
            setAchievementsToShow(JSON.parse(achievementsData));
            localStorage.removeItem("newAchievementsToShow");
        }
      }, POPUP_DELAY);
    }
    return () => { if (popupTimerId) clearTimeout(popupTimerId); };
  }, [isDashboardLoading, accountStatus]);

  // ========================================================================
  // FEEDBACK POPUP LOGIC
  // ========================================================================
  useEffect(() => {
    if (!FEEDBACK_POPUP_ENABLED) return;
    if (!accountStatus || isDashboardLoading) return;

    // Już pytaliśmy?
    const feedbackDone = localStorage.getItem('feedback_prompt_done');
    if (feedbackDone) return;

    // Ankieta po zakupie jest ważniejsza i celuje w tego samego usera (po zakupie
    // access_expires_at leci rok w przód, więc warunek "w trakcie trialu" niżej
    // przepuszcza też kupujących). Nigdy dwa popupy naraz.
    if (dashboardData?.show_purchase_survey) return;

    // Minimum 2 dni serii i 15 zadań
    if (accountStatus.current_streak < 2) return;
    if (totalProblems < 15) return;

    // Sprawdź czy user jest na trialu (nie kupił)
    const expiresAt = accountStatus.access_expires_at_timestamp;
    if (!expiresAt) return;

    const trialStartTimestamp = expiresAt - (72 * 3600);
    const hoursSinceStart = (Date.now() / 1000 - trialStartTimestamp) / 3600;

    // Tylko w trakcie trialu (0-72h)
    if (hoursSinceStart > 72) return;

    // Nie koliduj z innymi popupami — większy delay
    const timer = setTimeout(() => {
      // Dodatkowe sprawdzenie czy inny popup nie jest otwarty
      const onboardingFlag = localStorage.getItem("showOnboarding");
      const newBadgeData = localStorage.getItem("newBadgeToShow");
      const achievementsData = localStorage.getItem("newAchievementsToShow");
      
      if (onboardingFlag === "true" || newBadgeData || achievementsData) return;

      setShowFeedback(true);
    }, POPUP_DELAY + 2000);

    return () => clearTimeout(timer);
  }, [accountStatus, isDashboardLoading, totalProblems, dashboardData]);

  // ========================================================================
  // ANKIETA PO ZAKUPIE
  // ========================================================================
  // O tym, KIEDY pytać, decyduje backend (`show_purchase_survey`): dopiero po
  // pierwszej sesji odbytej już jako premium, z fallbackiem po 24h. Tu zostaje
  // samo ustąpienie miejsca innym popupom — badge i osiągnięcia z właśnie
  // zakończonej sesji mają pierwszeństwo, ankieta wróci po kolejnej.
  useEffect(() => {
    if (isDashboardLoading) return;
    if (!dashboardData?.show_purchase_survey) return;

    const timer = setTimeout(() => {
      const onboardingFlag = localStorage.getItem("showOnboarding");
      const newBadgeData = localStorage.getItem("newBadgeToShow");
      const achievementsData = localStorage.getItem("newAchievementsToShow");

      if (onboardingFlag === "true" || newBadgeData || achievementsData) return;

      setShowPurchaseSurvey(true);
    }, POPUP_DELAY + 2000);

    return () => clearTimeout(timer);
  }, [dashboardData, isDashboardLoading]);

  // --- SMART UX: OBLICZANIE STANU WIDGETU ---
const activeMilestoneState = useMemo(() => {
    // Roadmapa i cel zaleza od aktywnego poziomu (PP/PR).
    const roadmap = getRoadmapData(activeDomain);
    const goalForDomain = activeDomain === "PR" ? currentGoalIdPr : currentGoalId;
    const activeGoalData = roadmap?.[goalForDomain.toUpperCase()]
        || roadmap?.[DEFAULT_GOAL_BY_DOMAIN[activeDomain].toUpperCase()];

    // Kamienie liczymy z zadan TEGO poziomu — problems_solved_total jest globalne.
    const solvedInDomain = sumSolvedInFields(availableFields);

    const defaultState = {
        current: solvedInDomain,
        total: 100,
        label: "Postęp zadań",
        type: "tasks"
    };
    if (!activeGoalData || !availableFields || availableFields.length === 0) return defaultState;
    for (const milestone of activeGoalData.milestones) {

        if (milestone.type === "total_problems") {
            if (solvedInDomain < milestone.count) {
                return {
                    current: solvedInDomain,
                    total: milestone.count,
                    label: "Rozwiązane zadania",
                    type: "tasks"
                };
            }
        }
        
        else if (milestone.type === "all_fields_rank") {
             const completedFieldsCount = availableFields.filter(f => hasRank(f, milestone.rankName)).length;
             const totalFieldsCount = availableFields.length;
             if (completedFieldsCount < totalFieldsCount) {
                 return {
                     current: completedFieldsCount,
                     total: totalFieldsCount,
                     label: `Wymagana ranga: ${milestone.rankName}`,
                     type: "ranks"
                 };
             }
        }
        else if (milestone.type === "all_fields_plus_top") {
             const baseCompleted = availableFields.every(f => hasRank(f, milestone.baseRank));

             if (!baseCompleted) {
                 return {
                     current: availableFields.filter(f => hasRank(f, milestone.baseRank)).length,
                     total: availableFields.length,
                     label: `Wbij wszędzie ${milestone.baseRank}`,
                     type: "ranks"
                 };
             }
             const topCompletedCount = availableFields.filter(f => hasRank(f, milestone.topRank)).length;
             if (topCompletedCount < milestone.topCount) {
                 return {
                     current: topCompletedCount,
                     total: milestone.topCount,
                     label: `Specjalizacja (${milestone.topRank})`,
                     type: "ranks"
                 };
             }
        }
    }
    return {
        current: 1,
        total: 1,
        label: "Cel osiągnięty! Gratulacje!",
        type: "tasks"
    };
}, [availableFields, currentGoalId, currentGoalIdPr, activeDomain]);
  // --- HANDLERY ---
  const handleGoalChange = (newGoalId, newDailyGoalCount = null, domain = null) => {
    // Cel zapisujemy na poziomie, dla ktorego zostal wybrany (domyslnie aktywny).
    if ((domain || activeDomain) === "PR") {
      setCurrentGoalIdPr(newGoalId);
    } else {
      setCurrentGoalId(newGoalId);
      localStorage.setItem('user_current_roadmap_goal', newGoalId);
    }
    if (newDailyGoalCount !== null) {
        setAccountStatus((prev) => ({
            ...prev,
            problems_daily_goal: newDailyGoalCount
        }));
    }
};
  
  // Etykieta celu — nazwy sa rozne dla podstawy i rozszerzenia.
  const getGoalLabel = (id, domain = activeDomain) => {
    const roadmap = getRoadmapData(domain);
    const fallback = DEFAULT_GOAL_BY_DOMAIN[domain] || "solid";
    const entry = roadmap[(id || fallback).toUpperCase()] || roadmap[fallback.toUpperCase()];
    return entry?.label || "Zdać solidnie";
};
  const handleSolveProblemsClick = (e) => {
    if (e) e.preventDefault();
    // Blokuj wg dostepu do AKTYWNEJ domeny (podstawa i rozszerzenie maja osobne daty).
    if (isAccessBlocked(accountStatus)) { setPaywallPopupOpen(true); return; }
    if (mode === "standard") {
      setIsSessionLoading(true);
      setIsSessionDataReady(false);
      sessionDataRef.current = null;
      fetchSessionData()
        .then(data => { sessionDataRef.current = data; setIsSessionDataReady(true); })
        .catch((err) => {
          setIsSessionLoading(false);
          // Serwer moze odmowic dostepu do poziomu (402) — wtedy paywall, nie cisza.
          if (isNoAccessError(err)) setPaywallPopupOpen(true);
        });
    } else {
      fetchSessionData()
        .then(data => finalizeSession(data))
        .catch(err => {
          console.error(err);
          if (isNoAccessError(err)) setPaywallPopupOpen(true);
        });
    }
  };
  // "Zaczynajmy!" w onboardingu ma od razu otwierac pierwsza sesje (samouczek),
  // a nie zostawiac nowego usera na pulpicie — tam co trzeci nie robil ani jednego zadania.
  const startFirstSession = () => {
    if (totalProblems === 0 && selectedFields.length > 0) handleSolveProblemsClick();
  };
  const handlePopupComplete = () => {
    const data = sessionDataRef.current;
    sessionDataRef.current = null;
    if (data) {
      // Nawigujemy NIE zdejmując overlayu — navigate odmontuje dashboard razem
      // z popupem w jednym commicie, więc przycisk "Rozwiązuj" nie zdąży mignąć.
      finalizeSession(data);
    } else {
      setIsSessionLoading(false);
      setIsSessionDataReady(false);
    }
  };
  
  const fetchSessionData = async () => {
    if (accountStatus) { 
        const statusToSave = {
            ...accountStatus,
            problems_solved_total: totalProblems 
        };
        localStorage.setItem("userStatusBeforeSession", JSON.stringify(statusToSave)); 
    }
    const statsPromise = api.get("user_stats_view/");
    const preferences = {
      level_modifier: mode === "standard" ? levelModifier : 0,
      mobile_problems: mobileProblems,
    };
    let payload = { preferences };
    if (mode === "standard") { payload.selected_fields = selectedFields.map((i) => i.value); } 
    else { payload.selected_subfields = selectedSubfields; }
    const problemsPromise = api.post("start_solve_problems/", payload);
    const [statsResponse, problemsResponse] = await Promise.all([statsPromise, problemsPromise]);
    const statsMapping = {};
    if (statsResponse.data?.radar_chart?.fields) {
      statsResponse.data.radar_chart.fields.forEach((stat) => { statsMapping[stat.field_id] = stat; });
    }
    localStorage.setItem("fullUserStatsBeforeSession", JSON.stringify(statsMapping));
    localStorage.setItem("sessionStartTime", Math.floor(Date.now() / 1000));
    return problemsResponse.data;
  };
  const finalizeSession = (sessionData) => {
    setUserEloDict(sessionData.user_elo_dict);
    setProblemsData(sessionData.final_problems);
    localStorage.setItem("userEloDict", JSON.stringify(sessionData.user_elo_dict));
    localStorage.setItem("problemsData", JSON.stringify(sessionData.final_problems));
    // Pierwsza sesja w zyciu konta zaczyna sie od samouczka. Zadania sa juz
    // w localStorage, wiec po nim wchodzimy w sesje bez ponownego pobierania.
    navigate(totalProblems === 0 ? "/tutorial" : "/solve-problem");
  };
  const handleSearchResultSelect = async (targetSubfieldId, targetFieldId) => {
    if (mode !== "custom") setMode("custom");
    let isRemoving = false;
    setSelectedSubfields((prev) => {
      if (prev.includes(targetSubfieldId)) {
        isRemoving = true;
        return prev.filter((id) => id !== targetSubfieldId);
      } else { return [...prev, targetSubfieldId]; }
    });
    if (!isRemoving && !fullyLoadedFields.includes(targetFieldId)) {
        try {
            const { data } = await api.get(`field-details/${targetFieldId}/`);
            setSubfieldsData((prev) => ({ ...prev, [targetFieldId]: data }));
            setFullyLoadedFields((prev) => [...prev, targetFieldId]);
        } catch (error) { console.error(error); }
    }
  };
  const handleClearSelection = () => setSelectedSubfields([]);
  const handleCategoryClick = async (fieldId) => {
    if (customSelectedFields.includes(fieldId)) { setCustomSelectedFields((prev) => prev.filter((id) => id !== fieldId)); return; }
    setCustomSelectedFields((prev) => [...prev, fieldId]);
    if (!fullyLoadedFields.includes(fieldId)) {
      setLoadingSubfields(fieldId);
      try {
        const { data } = await axios.get(`${apiUrl}field-details/${fieldId}/`, { headers: { Authorization: `Token ${token}` }, });
        setSubfieldsData((prev) => ({ ...prev, [fieldId]: data }));
        setFullyLoadedFields((prev) => [...prev, fieldId]);
      } catch (error) { console.error(error); } finally { setLoadingSubfields(null); }
    }
  };
  const toggleSummaryExpansion = (e, fieldId) => { e.stopPropagation(); setExpandedSummaryIds((prevIds) => prevIds.includes(fieldId) ? prevIds.filter((id) => id !== fieldId) : [...prevIds, fieldId]); };
  const handleSubfieldToggle = (subfieldId) => { setSelectedSubfields((prev) => prev.includes(subfieldId) ? prev.filter((id) => id !== subfieldId) : [...prev, subfieldId]); };
  const handleToggleAllSubfields = (fieldId, selectAll = true) => {
    const subfieldsInCategory = subfieldsData[fieldId];
    if (!subfieldsInCategory) return;
    const allIds = subfieldsInCategory.map((sub) => sub.subfield_id);
    setSelectedSubfields((prev) => selectAll ? Array.from(new Set([...prev, ...allIds])) : prev.filter((id) => !allIds.includes(id)));
  };
  const getSelectedSubfieldsForCategory = (fieldId) => { return subfieldsData[fieldId] ? subfieldsData[fieldId].filter((sub) => selectedSubfields.includes(sub.subfield_id)) : []; };
  const handleSubscribeClick = () => { setPaywallPopupOpen(false); navigate("/upgrade"); };
  
  const handleUpdateDailyGoal = async (newGoal) => {
    try {
      const { data } = await api.patch("update-daily-goal/", { daily_goal: newGoal });
      setAccountStatus((prev) => ({ ...prev, problems_daily_goal: data.problems_daily_goal }));
    } catch (error) { console.error(error); }
  };
  const handleSearchTrigger = async () => {
    if (searchData.length > 0) {
        setSearchModalOpen(true);
        return;
    }
    setIsSearchLoading(true);
    try {
        console.log("Fetching search data on demand...");
        const { data } = await api.get('/training-search-data/');
        setSearchData(data);
        setSearchModalOpen(true);
    } catch (error) {
        console.error("Błąd pobierania searcha:", error);
    } finally {
        setIsSearchLoading(false);
    }
  };
  if (isDashboardLoading) return <InitialLoader />;

  return (
    <Page
      showNav
      accountStatus={accountStatus}
  >
      <div className="bg-white lg:bg-transparent flex-1 w-full flex flex-col">
      {username ? (
        <div className="flex flex-col max-w-2xl mx-auto w-full flex-grow px-4 lg:px-0">
        {/* Elastyczne wypełnienie góry (tylko mobile) — równoważy treść w pionie; zwija się do 0 gdy treść jest wysoka */}
        <div className="flex-grow lg:hidden" aria-hidden="true" />
        <div className="w-full lg:px-10 pt-[3vh] lg:py-10 lg:mt-[8vh] lg:bg-white lg:rounded-[3rem] lg:shadow-[4px_4px_12px_rgba(0,0,0,0.30)] lg:border lg:border-gray-200">

            <div className="mb-5 text-center">
              <h1 className="text-2xl font-apotek tracking-wider text-[#233855] uppercase leading-none ph-no-capture">
                Cześć, {username}!
              </h1>
              {accountStatus && (
                <p className="mt-1.5 text-base text-gray-500 leading-snug">
                  {getDashboardSubtitle(accountStatus)}
                </p>
              )}
            </div>

            <DashboardForm
              mode={mode}
              setMode={setMode}
              onSubmit={handleSolveProblemsClick}
              selectedFields={selectedFields}
              setSelectedFields={setSelectedFields}
              availableFields={availableFields}
              levelModifier={levelModifier}
              setLevelModifier={setLevelModifier}
              mobileProblems={mobileProblems}
              setMobileProblems={setMobileProblems}
              handleCategoryClick={handleCategoryClick}
              customSelectedFields={customSelectedFields}
              loadingSubfields={loadingSubfields}
              subfieldsData={subfieldsData}
              selectedSubfields={selectedSubfields}
              handleSubfieldToggle={handleSubfieldToggle}
              handleToggleAllSubfields={handleToggleAllSubfields}
              getSelectedSubfieldsForCategory={getSelectedSubfieldsForCategory}
              toggleSummaryExpansion={toggleSummaryExpansion}
              expandedSummaryIds={expandedSummaryIds}
              onOpenSearch={handleSearchTrigger}
              isSearchLoading={isSearchLoading}
              selectedCount={selectedSubfields.length}
              trialExpiryTimestamp={accountStatus?.access_expires_at_timestamp}
              
              GoalWidget={
                  <GoalProgressWidget
                    goalName={getGoalLabel(activeDomain === "PR" ? currentGoalIdPr : currentGoalId)}
                    current={activeMilestoneState.current}
                    total={activeMilestoneState.total}
                    label={activeMilestoneState.label}
                    type={activeMilestoneState.type}
                    onClick={() => setIsRoadmapOpen(true)}
                    streak={accountStatus?.current_streak ?? 0}
                    badgeImage={badgeStatus?.image}
                    badgeTextColor={badgeStatus?.textColor}
                    solvedToday={accountStatus?.problems_solved_today ?? 0}
                    dailyGoal={accountStatus?.problems_daily_goal ?? 1}
                    freezes={accountStatus?.streak_freezes ?? 0}
                  />
              }
            />
        </div>
        {/* Elastyczne wypełnienie dołu (mobile) — wchłonie martwą przestrzeń, zwija się do 0 gdy treść wysoka */}
        <div className="flex-grow lg:hidden" aria-hidden="true" />
        <div className="hidden lg:block h-[13vh] flex-shrink-0" />
        </div>
      ) : (
        <div className="text-center py-20">Loading...</div>
      )}
      </div>
      <GoalRoadmapModal
          isOpen={isRoadmapOpen}
          onClose={() => setIsRoadmapOpen(false)}
          currentGoalId={currentGoalId}
          currentGoalIdPr={currentGoalIdPr}
          onSaveGoal={handleGoalChange}
          userStats={{ total_problems: totalProblems }}
          availableFields={availableFields}
          dailyGoal={accountStatus?.problems_daily_goal ?? 1}
          onUpdateDailyGoal={handleUpdateDailyGoal}
          streak={accountStatus?.current_streak ?? 0}
          freezes={accountStatus?.streak_freezes ?? 0}
          activeDomain={accountStatus?.active_domain ?? "PP"}
      />
      {achievementsToShow && <AchievementPopup achievements={achievementsToShow} onClose={() => setAchievementsToShow(null)} />}
      {badgePopupData && <BadgeAchievementPopup badgeData={badgePopupData} onClose={() => setBadgePopupData(null)} />}
      {showOnboarding && (
        <OnboardingPopup
          onClose={(result) => {
            setShowOnboarding(false);
            if (result?.showDomainIntro) setShowDomainIntro(true);
            else startFirstSession();
          }}
          nickname={username}
          onGoalUpdate={handleGoalChange}
        />
      )}
      <DomainStartPopup
          isOpen={showDomainIntro}
          onClose={() => { setShowDomainIntro(false); startFirstSession(); }}
      />
      <PaywallPopup
          isOpen={isPaywallPopupOpen}
          onClose={() => setPaywallPopupOpen(false)}
          onSubscribe={handleSubscribeClick}
          userData={popupUserData}
      />
      <FeedbackPopup
          isOpen={showFeedback}
          onClose={() => setShowFeedback(false)}
      />
      <PurchaseSurveyPopup
          isOpen={showPurchaseSurvey}
          onClose={() => setShowPurchaseSurvey(false)}
      />
      <SearchModal isOpen={isSearchModalOpen} onClose={() => setSearchModalOpen(false)} data={searchData} onSelect={handleSearchResultSelect} currentSelection={selectedSubfields} />
      <SessionSetupPopup isOpen={isSessionLoading} isDataReady={isSessionDataReady} onComplete={handlePopupComplete} />
    </Page>
  );
};
export default Dashboard;